import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Paper, Grid, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, Snackbar, FormControl, InputLabel, Select, MenuItem,
  Card, CardContent, Divider, Pagination, CircularProgress, Tooltip, Chip
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon,
  Refresh as RefreshIcon, Save as SaveIcon, Cancel as CancelIcon, History as HistoryIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import { ContractInformation, SiteTypeRecord, ContractModel, ContractQueryModel, SiteTypeModifyRecord } from '../../types/contract';
import PageLayout from '../../components/Layout/PageLayout';
import { useTranslation } from '../../hooks/useTranslation';
import { useTableSort } from '../../hooks/useTableSort';
import SortableTableHead from '../../components/Table/SortableTableHead';
import { useRole } from '../../hooks/useRole';

const ContractDataPage: React.FC = () => {
  const { t } = useTranslation();
  const { isAdminOrManager, isStaff } = useRole();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [contracts, setContracts] = useState<ContractModel>();
  const [isCreateContract, setIsCreateContract] = useState(false);

  // 表格排序功能
  const getValue = (item: ContractInformation, key: string) => {
    switch (key) {
      case 'contractNo':
        return item.contract_No;
      case 'siteType':
        return item.site_Type;
      case 'effectiveDate':
        return new Date(item.full_Time_Effective_Date || item.on_Call_Effective_Date || item.off_Site_Effective_Date || '');
      case 'changedOn':
        return new Date(item.edit_DateTime || '');
      // case 'changedBy':
      //   return item.CreatedBy;
      default:
        return '';
    }
  };

  const { sortConfig, handleSort, sortedData } = useTableSort(contracts?.ContractInformations || [], getValue);
  const [selectedContract, setSelectedContract] = useState<ContractInformation  | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' | 'warning' });
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);
  const [deleteRecordIndex, setDeleteRecordIndex] = useState<number | null>(null);

  // 修改記錄相關狀態
  const [openModifyRecordsDialog, setOpenModifyRecordsDialog] = useState(false);
  const [modifyRecords, setModifyRecords] = useState<SiteTypeModifyRecord[]>([]);
  const [selectedContractNo, setSelectedContractNo] = useState<string>('');
  const [loadingModifyRecords, setLoadingModifyRecords] = useState(false);

  // 查詢條件
  const [queryParams, setQueryParams] = useState<ContractQueryModel>({
    Contract_No: '',
    Site_Type: '',
    Page: 1,
    PageSize: 20
  });

  // 合約表單
  const [contractForm, setContractForm] = useState<ContractInformation>({
    contract_No: '',
    psp: null,
    pe: null,
    site_Tel_No: null,
    job_Title: null,
    site_Type: null,
    on_Call_Effective_Date: null,
    full_Time_Effective_Date: null,
    off_Site_Effective_Date: null,
    siteTypeRecords : null,
    edit_DateTime: new Date().toISOString()
  });

  // Site Type Records 狀態
  const [siteTypeRecords, setSiteTypeRecords] = useState<SiteTypeRecord[]>([]);

    // 分頁狀態
    const [currentPage, setCurrentPage] = useState(1);
    //const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = 20;

  // 載入合約列表
  const loadContracts = async () => {
    try {
      setLoading(true);
      const response = await apiService.getContracts(queryParams);

      setContracts({
        ContractInformations: response.contractInformation,
      } as ContractModel);

      setTotalCount(response.totalCount);
      setCurrentPage(response.page);

    } catch (error) {
      console.error('載入合約失敗:', error);
      showSnackbar('載入合約失敗', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 處理分頁變化
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setQueryParams({ ...queryParams, Page: page });
  };

  // 載入特定合約
  const loadContractByNo = async (contractNo: string) => {
    try {
      setLoading(true);
      const contractModel = await apiService.getContractByNo(contractNo);
      console.log(contractModel);
      const contract = contractModel.ContractInformation;
      // 設置 siteTypeRecords 到 contract 中
      contract.siteTypeRecords = contractModel.SiteTypeRecords || [];
      setSelectedContract(contract);
      setContractForm(contract);
      setSiteTypeRecords(contractModel.SiteTypeRecords || []);
      setOpenDialog(true); // 直接打開編輯對話框
    } catch (error) {
      console.error('載入合約失敗:', error);
      showSnackbar('載入合約失敗', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 保存合約
  const handleSaveContract = async () => {
    console.log(contractForm);
    console.log(siteTypeRecords);
    try {
      setLoading(true);

      if (isCreateContract) {
        await apiService.createContract(contractForm);
      } else {
        await apiService.updateContract(contractForm.contract_No, contractForm);
      }

      showSnackbar('合約保存成功', 'success');
      setOpenDialog(false);
      loadContracts();
    } catch (error) {
      console.error('保存合約失敗:', error);
      showSnackbar('保存合約失敗', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 刪除 Site Type Record
  const handleDeleteSiteType = async (id: number) => {
    if (!window.confirm('確定要刪除這個 Site Type 記錄嗎？')) {
      return;
    }

    try {
      setLoading(true);
      await apiService.deleteSiteTypeRecord(id);
      showSnackbar('Site Type 記錄刪除成功', 'success');
      if (selectedContract) {
        loadContractByNo(selectedContract.contract_No);
      }
    } catch (error) {
      console.error('刪除 Site Type 記錄失敗:', error);
      showSnackbar('刪除 Site Type 記錄失敗', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 根據合約的當前 site_Type 獲取對應的生效日期
  const getEffectiveDateBySiteType = (contract: ContractInformation, siteTypeRecords: SiteTypeRecord[]) => {
    if (!contract.site_Type || !siteTypeRecords || siteTypeRecords.length === 0) {
      return '';
    }

    // 根據合約的當前 site_Type 找到對應的記錄
    const matchingRecord = siteTypeRecords.find(record =>
      record.site_Type === contract.site_Type && record.showOrNotShow !== 1
    );

    if (matchingRecord) {
      return matchingRecord.effective_Date || '';
    }

    // 如果沒有找到對應的記錄，返回合約中的對應日期字段
    switch (contract.site_Type) {
      case 'Full Time':
        return contract.full_Time_Effective_Date || '';
      case 'On Call':
        return contract.on_Call_Effective_Date || '';
      case 'Off-Site':
        return contract.off_Site_Effective_Date || '';
      default:
        return '';
    }
  };

  // 獲取合約的修改人（從 siteTypeRecords 中獲取）
  const getLastChangedBy = (contract: ContractInformation, siteTypeRecords: SiteTypeRecord[]) => {
    if (!siteTypeRecords || siteTypeRecords.length === 0) {
      return  '-';
    }

    // 找到與當前 site_Type 匹配的記錄
    const matchingRecord = siteTypeRecords.find(record =>
      record.site_Type === contract.site_Type && record.showOrNotShow !== 0
    );

    if (matchingRecord && matchingRecord.changed_By) {
      return matchingRecord.changed_By;
    }

    // 如果沒有找到匹配的記錄，返回最新的修改人
    const latestRecord = siteTypeRecords
      .filter(record => record.showOrNotShow !== 0)
      .sort((a, b) => new Date(b.changed_On || '').getTime() - new Date(a.changed_On || '').getTime())[0];

    return latestRecord?.changed_By ||  '-';
  };

  // 獲取合約對應的 siteTypeRecords
  const getContractSiteTypeRecords = (contractNo: string, allSiteTypeRecords: SiteTypeRecord[]) => {
    if (!contractNo || !allSiteTypeRecords) {
      return [];
    }

    return allSiteTypeRecords.filter(record =>
      record.contract_No === contractNo && record.showOrNotShow !== 0
    );
  };

  // 根據 effective date 獲取 siteTypeRecords 的記錄（選擇最新的 effective date）
  const getRecordByEffectiveDate = (siteTypeRecords: SiteTypeRecord[]) => {
    if (!siteTypeRecords || siteTypeRecords.length === 0) {
      return null;
    }

    // 過濾有效的記錄並按 effective date 排序（最新的在前）
    const validRecords = siteTypeRecords
      .filter(record => record.showOrNotShow !== 0 && record.effective_Date)
      .sort((a, b) => {
        const dateA = new Date(a.effective_Date || '').getTime();
        const dateB = new Date(b.effective_Date || '').getTime();
        return dateB - dateA; // 降序排序，最新的在前
      });

    if (validRecords.length === 0) {
      return null;
    }

    // 返回最新的記錄（effective date 最大的）
    const latestRecord = validRecords[0];
    return {
      siteType: latestRecord.site_Type,
      effectiveDate: latestRecord.effective_Date,
      changedOn: latestRecord.changed_On || ''
    };
  };

  // 打開編輯 Site Type 對話框
  const handleEditSiteType = (contract: ContractInformation) => {
    console.log(contract);


    setContractForm(contract);
    setSiteTypeRecords(contract.siteTypeRecords || []);
    setOpenDialog(true);
  };


  // 添加 Site Type Record
  const handleAddSiteTypeRecord = () => {

    const newRecord: SiteTypeRecord = {
      id: null,
      contract_No: contractForm.contract_No,
      site_Type: '',
      effective_Date: '',
      changed_By: user?.userId || '',
      showOrNotShow: 1,
      isModified: true
    };

    setContractForm({...contractForm,siteTypeRecords: [...siteTypeRecords, newRecord]});
    setSiteTypeRecords([...siteTypeRecords, newRecord]);
  };

  // 更新 Site Type Record
  const handleUpdateSiteTypeRecord = (index: number, field: keyof SiteTypeRecord, value: any) => {
    const updatedRecords = [...siteTypeRecords];
    updatedRecords[index] = {
      ...updatedRecords[index],
      [field]: value,
      isModified: true
    };
    setContractForm({...contractForm,siteTypeRecords: updatedRecords});
    setSiteTypeRecords(updatedRecords);
  };

  // 顯示刪除確認對話框
  const handleDeleteSiteTypeRecord = (index: number) => {
    setDeleteRecordIndex(index);
    setOpenDeleteConfirmDialog(true);
  };

  // 確認刪除 Site Type Record
  const confirmDeleteSiteTypeRecord = async () => {
    if (deleteRecordIndex === null) {
      setOpenDeleteConfirmDialog(false);
      return;
    }

    const record = siteTypeRecords[deleteRecordIndex];

    try {
      if (record.id && record.id > 0) {
        // 如果已存在，調用 API 刪除
        await apiService.deleteSiteTypeRecord(record.id);
        showSnackbar('Site Type Record 刪除成功', 'success');
      }

      // 從本地狀態中移除（無論是否調用 API）
      const updatedRecords = [...siteTypeRecords];
      updatedRecords.splice(deleteRecordIndex, 1);
      setContractForm({...contractForm, siteTypeRecords: updatedRecords});
      setSiteTypeRecords(updatedRecords);

      setOpenDeleteConfirmDialog(false);
      setDeleteRecordIndex(null);
    } catch (error: any) {
      console.error('Error deleting site type record:', error);
      showSnackbar(
        error?.response?.data?.message || '刪除 Site Type Record 失敗',
        'error'
      );
      setOpenDeleteConfirmDialog(false);
      setDeleteRecordIndex(null);
    }
  };

  // 取消刪除
  const cancelDeleteSiteTypeRecord = () => {
    setOpenDeleteConfirmDialog(false);
    setDeleteRecordIndex(null);
  };

  // 查看修改記錄
  const handleViewModifyRecords = async (contractNo: string) => {
    try {
      setLoadingModifyRecords(true);
      setSelectedContractNo(contractNo);
      const records = await apiService.getContractModifyRecords(contractNo);
      setModifyRecords(records);
      setOpenModifyRecordsDialog(true);
    } catch (error) {
      console.error('載入修改記錄失敗:', error);
      showSnackbar('載入修改記錄失敗', 'error');
    } finally {
      setLoadingModifyRecords(false);
    }
  };

  // 顯示通知
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  // 初始化載入
  useEffect(() => {
    loadContracts();
  }, []);

  // 當查詢參數變化時重新載入
  useEffect(() => {
    loadContracts();
  }, [queryParams.Page]);

  return (
    <PageLayout title={t('pages.contractData.title')}>
      <Container maxWidth="xl">

        {/* 查詢條件 */}
        <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            {t('pages.contractData.queryConditions')}
          </Typography>
          <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setContractForm({
                  contract_No: '',
                  psp: null,
                  pe: null,
                  site_Tel_No:null,
                  job_Title: null,
                  site_Type: null,
                  on_Call_Effective_Date: null,
                  full_Time_Effective_Date: null,
                  off_Site_Effective_Date: null,
                  edit_DateTime: new Date().toISOString()
                });
                setIsCreateContract(true);
                setSiteTypeRecords([]); // 新增合約時清空 Site Type Records
                setOpenDialog(true);
              }}
              disabled={!isAdminOrManager}
            >
              {t('pages.contractData.addContract')}
            </Button>
          </Box>
          <Grid container spacing={3} alignItems="center" sx={{ justifyContent: 'space-between' }}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  disabled={isStaff}
                  fullWidth
                  label={t('pages.contractData.fields.contractNo')}
                  value={isStaff ? user?.userId : queryParams.Contract_No}
                  onChange={(e) => setQueryParams({ ...queryParams, Contract_No: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !loading && !isStaff) {
                      e.preventDefault();
                      setCurrentPage(1);
                      loadContracts();
                    }
                  }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('pages.contractData.fields.siteType')}</InputLabel>
                  <Select
                    value={queryParams.Site_Type}
                    onChange={(e) => setQueryParams({ ...queryParams, Site_Type: e.target.value })}
                    label={t('pages.contractData.fields.siteType')}
                  >
                    <MenuItem value="">{t('common.all')}</MenuItem>
                    <MenuItem value="Full Time">{t('pages.contractData.siteTypes.fullTime')}</MenuItem>
                    <MenuItem value="On Call">{t('pages.contractData.siteTypes.onCall')}</MenuItem>
                    <MenuItem value="Off-Site">{t('pages.contractData.siteTypes.offSite')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            <Grid item xs={12} sm={6} md={3} sx={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                onClick={loadContracts}
                disabled={loading}
                sx={{ mr: 2 }}
              >
                {loading ? t('common.loading') : t('common.query')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  setQueryParams({ Contract_No: '', Site_Type: '', Page: 1, PageSize: 20 });
                  loadContracts();
                }}
                disabled={loading}
              >
                {t('common.reset')}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* 數據表格 */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2, alignSelf: 'center' }}>
              {t('common.loading')}
            </Typography>
          </Box>
        )}

        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 600,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '4px',
              '&:hover': {
                background: '#a8a8a8',
              },
            },
          }}
        >
          <Table stickyHeader>
            <SortableTableHead
              columns={[
                { key: 'contractNo', label: t('pages.contractData.fields.contractNo') },
                { key: 'siteType', label: t('pages.contractData.fields.siteType') },
                { key: 'effectiveDate', label: t('pages.contractData.fields.effectiveDate') },
                { key: 'changedOn', label: t('pages.contractData.fields.changedOn') },
                { key: 'changedBy', label: t('pages.contractData.fields.changedBy') },
                { key: 'action', label: t('common.action'), sortable: false }
              ]}
              sortConfig={sortConfig}
              onSort={handleSort}
              stickyHeader={true}
            />
            <TableBody>
              {sortedData.map((contract, index) => {
                // 獲取該合約的 siteTypeRecords
                const contractSiteTypeRecords = getContractSiteTypeRecords(contract.contract_No, contract.siteTypeRecords || []);

                return (
                  <TableRow
                    key={contract.contract_No}
                    sx={{
                      backgroundColor: index % 2 === 0 ? 'background.paper' : 'grey.50',
                      transition: 'all 0.2s ease-in-out',
                      '& .MuiTableCell-root': {
                        borderBottom: '1px solid',
                        borderBottomColor: 'divider',
                        padding: '12px 16px',
                      }
                    }}
                  >
                    <TableCell sx={{
                      fontFamily: 'monospace',
                      fontWeight: 'medium',
                      // color: 'primary.main'
                    }}>
                      {contract.contract_No}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const recordByDate = getRecordByEffectiveDate(contractSiteTypeRecords);
                        return (
                          <Chip
                            label={recordByDate?.siteType || contract.site_Type || '-'}
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: 'medium' }}
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const recordByDate = getRecordByEffectiveDate(contractSiteTypeRecords);
                        return recordByDate?.effectiveDate
                          ? new Date(recordByDate.effectiveDate).toLocaleDateString('en-GB')
                          : (getEffectiveDateBySiteType(contract, contractSiteTypeRecords)
                            ? new Date(getEffectiveDateBySiteType(contract, contractSiteTypeRecords)).toLocaleDateString('en-GB')
                            : '-');
                      })()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const recordByDate = getRecordByEffectiveDate(contractSiteTypeRecords);
                        return recordByDate?.changedOn
                          ? new Date(recordByDate.changedOn).toLocaleString('en-GB')
                          : '-';
                      })()}
                    </TableCell>
                    <TableCell>{getLastChangedBy(contract, contractSiteTypeRecords)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title={t('pages.contractData.editContract')}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setIsCreateContract(false);
                              handleEditSiteType(contract);
                            }}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'primary.light',
                                color: 'white',
                              }
                            }}
                            disabled={!isAdminOrManager}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('pages.contractData.viewModifyRecords')}>
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleViewModifyRecords(contract.contract_No)}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'info.light',
                                color: 'white',
                              }
                            }}
                          >
                            <HistoryIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* 分頁 */}
        {totalCount > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={Math.ceil(totalCount / 20)}
              page={queryParams.Page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}

        {/* 合約編輯對話框 */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="lg" fullWidth>
          <DialogTitle>{!isCreateContract ? t('pages.contractData.editContract') : t('pages.contractData.addContract')}</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* 合約基本信息 */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  {t('pages.contractData.contractInfo')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  disabled={!isCreateContract}
                  fullWidth
                  label={t('pages.contractData.fields.contractNo')}
                  value={contractForm.contract_No}
                  onChange={(e) => setContractForm({ ...contractForm, contract_No: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Site Type</InputLabel>
                  <Select
                    value={contractForm.site_Type}
                    onChange={(e) => setContractForm({ ...contractForm, site_Type: e.target.value })}
                    label="Site Type"
                    required
                  >
                    <MenuItem value="Full Time">Full Time</MenuItem>
                    <MenuItem value="On Call">On Call</MenuItem>
                    <MenuItem value="Off-Site">Off Site</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="PSP"
                  value={contractForm.psp}
                  onChange={(e) => setContractForm({ ...contractForm, psp: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="PE"
                  value={contractForm.pe}
                  onChange={(e) => setContractForm({ ...contractForm, pe: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('pages.contractData.fields.siteTelNo')}
                  value={contractForm.site_Tel_No}
                  onChange={(e) => setContractForm({ ...contractForm, site_Tel_No: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('pages.contractData.fields.jobTitle')}
                  value={contractForm.job_Title}
                  onChange={(e) => setContractForm({ ...contractForm, job_Title: e.target.value })}
                />
              </Grid>

              {/* Site Type Records 管理 */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    {t('pages.contractData.siteTypeRecords')}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddSiteTypeRecord}
                    size="small"
                  >
                     {t('pages.contractData.addSiteTypeRecord')}
                  </Button>
                </Box>
              </Grid>

              {siteTypeRecords.map((record, index) => (
                <React.Fragment key={index}>
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth>
                      <InputLabel>Site Type</InputLabel>
                      <Select
                        value={record.site_Type}
                        onChange={(e) => handleUpdateSiteTypeRecord(index, 'site_Type', e.target.value)}
                        label="Site Type"
                      >
                        <MenuItem value="Full Time">Full Time</MenuItem>
                        <MenuItem value="On Call">On Call</MenuItem>
                        <MenuItem value="Off-Site">Off-Site</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label={t('pages.contractData.fields.effectiveDate')}
                      type="date"
                      value={record.effective_Date ? new Date(record.effective_Date).toISOString().split('T')[0] : ''}
                      onChange={(e) => handleUpdateSiteTypeRecord(index, 'effective_Date', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      disabled
                      fullWidth
                      label={t('pages.contractData.fields.changedOn')}
                      value={record.changed_On ? new Date(record.changed_On).toLocaleString('en-GB') : '-'}
                    />
                  </Grid>
                  <Grid item xs={8} sm={2}>
                    <TextField
                      disabled
                      fullWidth
                      label={t('pages.contractData.fields.changedBy')}
                      value={record.changed_By}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                      <Tooltip title="刪除">
                        <IconButton onClick={() => handleDeleteSiteTypeRecord(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>{t('common.cancel')}</Button>
            <Button variant="contained" onClick={handleSaveContract} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : t('common.save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Site Type 編輯對話框 */}
        {/* <Dialog open={openSiteTypeDialog} onClose={() => setOpenSiteTypeDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {siteTypeForm.id === 0 ? t('pages.contractData.addSiteTypeRecord') : t('pages.contractData.editSiteTypeRecord')}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="合約編號"
                  value={siteTypeForm.contract_No}
                  onChange={(e) => setSiteTypeForm({ ...siteTypeForm, contract_No: e.target.value })}
                  required
                  disabled={siteTypeForm.id !== 0} // 編輯時不允許修改合約編號
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Site Type</InputLabel>
                  <Select
                    value={siteTypeForm.site_Type}
                    onChange={(e) => setSiteTypeForm({ ...siteTypeForm, site_Type: e.target.value })}
                    label="Site Type"
                    required
                  >
                    <MenuItem value="Full Time">Full Time</MenuItem>
                    <MenuItem value="On Call">On Call</MenuItem>
                    <MenuItem value="Off-Site">Off Site</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="生效日期"
                  type="date"
                  value={siteTypeForm.effective_Date}
                  onChange={(e) => setSiteTypeForm({ ...siteTypeForm, effective_Date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenSiteTypeDialog(false)}>取消</Button>
            <Button variant="contained" onClick={handleSaveSiteType} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : '保存'}
            </Button>
          </DialogActions>
        </Dialog> */}

        {/* 修改記錄對話框 */}
        <Dialog
          open={openModifyRecordsDialog}
          onClose={() => setOpenModifyRecordsDialog(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
              {t('pages.contractData.modifyRecords')} - {selectedContractNo}
            </Typography>
          </DialogTitle>
          <DialogContent>
            {loadingModifyRecords ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : modifyRecords.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  {t('pages.contractData.noModifyRecords')}
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'primary.light' }}>
                      <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>
                        {t('pages.contractData.fields.changedOn')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>
                        {t('pages.contractData.fields.changedRoleBy')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>
                        {t('pages.contractData.fields.modifyAction')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {modifyRecords.map((record, index) => (
                      <TableRow
                        key={record.id}
                        sx={{
                          backgroundColor: index % 2 === 0 ? 'background.paper' : 'grey.50',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <TableCell>
                          {record.changed_On
                            ? new Date(record.changed_On).toLocaleString('zh-TW', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                              })
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={record.changed_Role_By || '-'}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {record.modify_Action}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModifyRecordsDialog(false)} variant="outlined">
              {t('common.close')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 通知 */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* 刪除確認對話框 */}
        <Dialog
          open={openDeleteConfirmDialog}
          onClose={cancelDeleteSiteTypeRecord}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            確認刪除
          </DialogTitle>
          <DialogContent>
            <Typography id="delete-dialog-description">
              您確定要刪除此 Site Type Record 嗎？此操作無法復原。
              {deleteRecordIndex !== null && siteTypeRecords[deleteRecordIndex] && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>合約編號：</strong>{siteTypeRecords[deleteRecordIndex].contract_No}<br />
                    <strong>Site Type：</strong>{siteTypeRecords[deleteRecordIndex].site_Type}
                  </Typography>
                </Box>
              )}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelDeleteSiteTypeRecord} color="inherit">
              取消
            </Button>
            <Button onClick={confirmDeleteSiteTypeRecord} color="error" variant="contained" autoFocus>
              刪除
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </PageLayout>
  );
};

export default ContractDataPage;